import { useCallback, useEffect, useRef, useState } from "react";
import { type Chat, type User } from "../types";

interface UseChatParticipantsArgs {
  currentUser: User | null | undefined;
  userChats: Chat[];
  getUsersByIds: (ids: string[]) => Promise<User[]>;
  getAllUsers: () => Promise<User[]>;
}

export function useChatParticipants({
  currentUser,
  userChats,
  getUsersByIds,
  getAllUsers,
}: UseChatParticipantsArgs) {
  const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
  const [isLoadingOnlineUsers, setIsLoadingOnlineUsers] = useState(true);
  const [participantUsersMap, setParticipantUsersMap] = useState<Map<string, User>>(new Map());
  const participantUsersCache = useRef<Map<string, User>>(new Map());
  const resolvedParticipantsCache = useRef<
    Map<string, { key: string; users: User[] }>
  >(new Map());
  const onlineFetchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshParticipants = useCallback(async () => {
    if (!currentUser) return;
    const allParticipantIds = new Set<string>(userChats.flatMap((chat) => chat.participants));
    allParticipantIds.delete(currentUser.id);
    const idsToFetch = Array.from(allParticipantIds).filter(
      (id) => !participantUsersCache.current.has(id),
    );
    if (idsToFetch.length > 0) {
      const users = await getUsersByIds(idsToFetch);
      users.forEach((u) => participantUsersCache.current.set(u.id, u));
    }
    setParticipantUsersMap(new Map(participantUsersCache.current));
  }, [currentUser, userChats, getUsersByIds]);

  const refreshOnlineUsers = useCallback(() => {
    if (onlineFetchTimeout.current) {
      clearTimeout(onlineFetchTimeout.current);
    }
    onlineFetchTimeout.current = setTimeout(async () => {
      if (!currentUser) return;
      try {
        const allUsers = await getAllUsers();
        const onlineFriends = allUsers.filter((u) => {
          const isFriend =
            currentUser.following?.includes(u.id) &&
            u.following?.includes(currentUser.id);
          return u.isOnline && u.id !== currentUser.id && isFriend;
        });
        setOnlineUsers(onlineFriends);
      } finally {
        setIsLoadingOnlineUsers(false);
      }
    }, 250);
  }, [currentUser, getAllUsers]);

  useEffect(() => {
    refreshParticipants();
    refreshOnlineUsers();
    return () => {
      if (onlineFetchTimeout.current) {
        clearTimeout(onlineFetchTimeout.current);
      }
    };
  }, [refreshParticipants, refreshOnlineUsers]);

  const resolveParticipants = useCallback(
    (chat: Chat, currentUserId?: string) => {
      const participantIds = chat.participants.filter((id) => id !== currentUserId);
      const resolvedUsers = participantIds
        .filter((id) => id !== currentUserId)
        .map((id) => participantUsersCache.current.get(id) || participantUsersMap.get(id))
        .filter((u): u is User => !!u);

      const cacheKey = `${currentUserId || ""}__${participantIds.join("|")}`;
      const cached = resolvedParticipantsCache.current.get(chat.id);
      if (
        cached?.key === cacheKey &&
        cached.users.length === resolvedUsers.length &&
        cached.users.every((user, index) => user === resolvedUsers[index])
      ) {
        return cached.users;
      }

      resolvedParticipantsCache.current.set(chat.id, {
        key: cacheKey,
        users: resolvedUsers,
      });

      return resolvedUsers;
    },
    [participantUsersMap],
  );

  return {
    onlineUsers,
    isLoadingOnlineUsers,
    participantUsersMap,
    resolveParticipants,
    refreshParticipants,
  };
}
